import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type {
  CalendarCustomView,
  CalendarEvent,
  CalendarEventStatus,
  CalendarRenderContext,
  ViewMode,
} from './types.js';
import { DEFAULT_KIND_THEME } from './utils/kindTheme.js';
import {
  CalendarI18nProvider,
  defaultCalendarMessages,
  type CalendarMessages,
} from './i18n.js';
import {
  defaultViewerTz,
  isoDate,
  todayPlain,
} from './utils/dateUtils.js';
import { applyCalendarFilters } from './utils/visibility.js';
import {
  eventInstanceKey,
  uniqueCalendarEvents,
  uniqueCalendarGroups,
} from './utils/identity.js';
import { FilterBar } from './components/FilterBar.js';
import { EventDetailSheet } from './components/EventDetailSheet.js';
import { CalendarFab } from './components/CalendarFab.js';
import { MonthView } from './views/MonthView.js';
import { MonthViewMobile } from './views/MonthViewMobile.js';
import { WeekView } from './views/WeekView.js';
import { WeekViewMobile } from './views/WeekViewMobile.js';
import { DayView } from './views/DayView.js';
import { AgendaView } from './views/AgendaView.js';
import { useContainerSize } from './hooks/useContainerSize.js';
import {
  BUILT_IN_VIEWS,
  viewLabel,
  type CalendarEventActionPermissions,
  type CalendarViewProps,
} from './calendar-view-props.js';
import {
  buildCalendarEventContextMenu,
  eventActionAllowed,
  type CalendarEventActionKey,
} from './utils/eventActions.js';
import { CalendarViewToolbar } from './calendar-view-toolbar.js';

export type { CalendarViewProps };

/** Top-level Calendar component. Picks a desktop-shaped or mobile-shaped
 *  variant for each view based on its container width (NOT the viewport),
 *  so a calendar embedded in a narrow sidebar still gets the phone UX.
 *
 *  Filters / cursor / selectedDate are uncontrolled by default; pair the
 *  `*FilterChange` / `onCursorChange` / `onSelectedDateChange` callbacks
 *  with their counterpart props to make them controlled (e.g. for URL
 *  state sync via TanStack Router search params).
 *
 *  On mobile, the layout swaps in:
 *  - dot-density month grid + selected-day list
 *  - week = day-strip + day list (no 7-col timed grid)
 *  - day = single-day timeline + day-strip header + swipe nav
 *  - agenda = unchanged (already the most mobile-friendly view)
 *  - filter chip strip → bottom sheet trigger
 *  - "New event" → bottom-right FAB
 *  - tap an event → detail bottom sheet (Open / Close) */
export function CalendarView(props: CalendarViewProps): ReactNode {
  const {
    events,
    kindTheme = DEFAULT_KIND_THEME,
    groups,
    defaultView,
    view: controlledView,
    onViewChange,
    today,
    cursor: controlledCursor,
    onCursorChange,
    defaultCursor,
    selectedDate: controlledSelected,
    onSelectedDateChange,
    defaultSelectedDate,
    kindFilter: controlledKindFilter,
    onKindFilterChange,
    groupFilter: controlledGroupFilter,
    onGroupFilterChange,
    statusFilter: controlledStatusFilter,
    onStatusFilterChange,
    onSelectEvent,
    onSelectLink,
    onCreateEvent,
    onUpdateEvent,
    onCancelEvent,
    onDeleteEvent,
    onDuplicateEvent,
    onEditEvent,
    eventActionPermissions,
    title,
    showToolbarTitle = true,
    showToolbarCreateButton = true,
    views = BUILT_IN_VIEWS,
    customViews,
    locale,
    weekStartsOn = 0,
    viewerTz: viewerTzProp,
    className,
    toolbarExtras,
    composer,
    enableICSExport = true,
    renderDayRailExtras,
    renderEventExtras,
    messages: messagesProp,
  } = props;

  // Merge consumer-supplied messages over the English defaults so missing
  // keys silently fall back to the original strings instead of displaying
  // an empty label. Memoised — every child reads the same reference, so
  // a new merge on each render would invalidate every memo downstream.
  const messages = useMemo<CalendarMessages>(
    () => ({ ...defaultCalendarMessages, ...messagesProp }),
    [messagesProp],
  );
  const resolvedTitle = title ?? messages.title;

  const viewerTz = useMemo(() => viewerTzProp ?? defaultViewerTz(), [viewerTzProp]);

  const containerRef = useRef<HTMLDivElement>(null);
  const tier = useContainerSize(containerRef);
  const isMobile = tier === 'mobile';

  // ---------- view (uncontrolled fallback + auto-mobile-default) ----------
  const [uncontrolledView, setUncontrolledView] = useState<ViewMode | string>(
    defaultView ?? 'month',
  );
  const [autoDefault, setAutoDefault] = useState(defaultView === undefined);
  useEffect(() => {
    if (autoDefault && controlledView === undefined) {
      setUncontrolledView(isMobile ? 'agenda' : 'month');
    }
  }, [isMobile, autoDefault, controlledView]);
  const view = controlledView ?? uncontrolledView;
  const setView = (next: ViewMode | string): void => {
    setAutoDefault(false);
    if (controlledView === undefined) setUncontrolledView(next);
    onViewChange?.(next);
  };

  // ---------- today / cursor / selectedDate ----------
  const todayKey = useMemo(() => {
    if (today !== undefined) return today;
    return isoDate(todayPlain(viewerTz));
  }, [today, viewerTz]);

  const [uncontrolledCursor, setUncontrolledCursor] = useState<string>(
    defaultCursor ?? todayKey,
  );
  const cursor = controlledCursor ?? uncontrolledCursor;
  const setCursor = (next: string): void => {
    if (controlledCursor === undefined) setUncontrolledCursor(next);
    onCursorChange?.(next);
  };

  const [uncontrolledSelected, setUncontrolledSelected] = useState<string>(
    defaultSelectedDate ?? todayKey,
  );
  const selectedDate = controlledSelected ?? uncontrolledSelected;
  const setSelectedDate = (next: string): void => {
    if (controlledSelected === undefined) setUncontrolledSelected(next);
    onSelectedDateChange?.(next);
  };

  // ---------- filters (uncontrolled fallback) ----------
  const [uncontrolledKind, setUncontrolledKind] = useState<ReadonlySet<string>>(new Set());
  const kindFilter = controlledKindFilter ?? uncontrolledKind;
  const setKindFilter = (next: Set<string>): void => {
    if (controlledKindFilter === undefined) setUncontrolledKind(next);
    onKindFilterChange?.(next);
  };

  const [uncontrolledGroup, setUncontrolledGroup] = useState<ReadonlySet<string>>(new Set());
  const groupFilter = controlledGroupFilter ?? uncontrolledGroup;
  const setGroupFilter = (next: Set<string>): void => {
    if (controlledGroupFilter === undefined) setUncontrolledGroup(next);
    onGroupFilterChange?.(next);
  };

  const [uncontrolledStatus, setUncontrolledStatus] = useState<
    ReadonlySet<CalendarEventStatus>
  >(new Set());
  const statusFilter = controlledStatusFilter ?? uncontrolledStatus;
  const setStatusFilter = (next: Set<CalendarEventStatus>): void => {
    if (controlledStatusFilter === undefined) setUncontrolledStatus(next);
    onStatusFilterChange?.(next);
  };

  // ---------- mobile event detail sheet ----------
  const [sheetEvent, setSheetEvent] = useState<CalendarEvent | null>(null);
  const [selectedEventKey, setSelectedEventKey] = useState<string | null>(null);

  const safeEvents = useMemo(() => uniqueCalendarEvents(events), [events]);
  const safeGroups = useMemo(
    () => (groups === undefined ? undefined : uniqueCalendarGroups(groups)),
    [groups],
  );

  const filteredEvents = useMemo(
    () =>
      applyCalendarFilters(safeEvents, {
        kinds: kindFilter,
        groups: groupFilter,
        statuses: statusFilter,
      }),
    [safeEvents, kindFilter, groupFilter, statusFilter],
  );

  const visibleKinds = useMemo(() => {
    const set = new Set<string>();
    safeEvents.forEach((e) => set.add(e.kind));
    return set;
  }, [safeEvents]);

  // ---------- view options (built-in + custom) ----------
  const customViewMap = useMemo(() => {
    const m = new Map<string, CalendarCustomView>();
    customViews?.forEach((v) => m.set(v.id, v));
    return m;
  }, [customViews]);

  const viewOptions = useMemo(() => {
    const all: { value: string; label: string }[] = [];
    views.forEach((v) => {
      if ((BUILT_IN_VIEWS as readonly string[]).includes(v)) {
        all.push({ value: v, label: viewLabel(messages, v as ViewMode) });
      } else if (customViewMap.has(v)) {
        all.push({ value: v, label: customViewMap.get(v)!.label });
      }
    });
    customViewMap.forEach((cv) => {
      if (!views.includes(cv.id)) all.push({ value: cv.id, label: cv.label });
    });
    return all;
  }, [views, customViewMap, messages]);

  const getActionPermissions = useCallback(
    (event: CalendarEvent): CalendarEventActionPermissions =>
      eventActionPermissions?.(event) ?? {},
    [eventActionPermissions],
  );
  const actionAllowed = useCallback(
    (
      event: CalendarEvent,
      key: CalendarEventActionKey,
    ): boolean => eventActionAllowed(getActionPermissions(event), key),
    [getActionPermissions],
  );

  // On mobile, intercept event taps → show the detail sheet only when
  // the consumer says the event can be opened. Read-only events still
  // become selected, but activation stops there.
  const handleEventTap = (event: CalendarEvent): void => {
    setSelectedEventKey(eventInstanceKey(event));
    if (!actionAllowed(event, 'canOpen')) return;
    if (isMobile) {
      setSheetEvent(event);
    } else {
      onSelectEvent?.(event);
    }
  };

  // Per-event right-click menu actions. We only build the actions
  // map when at least one mutator is wired; otherwise we pass undefined
  // so the menu doesn't render at all.
  const hasAnyContextAction =
    onSelectEvent !== undefined ||
    onEditEvent !== undefined ||
    onDuplicateEvent !== undefined ||
    onCancelEvent !== undefined ||
    onDeleteEvent !== undefined;

  const buildContextMenu = useMemo(() => {
    return buildCalendarEventContextMenu({
      hasAnyContextAction,
      messages,
      actionAllowed,
      onSelectEvent,
      onEditEvent,
      onDuplicateEvent,
      onCancelEvent,
      onDeleteEvent,
    });
    // The functions are stable from the consumer; we recompute only
    // when the *set* of provided handlers changes.
  }, [
    hasAnyContextAction,
    messages,
    actionAllowed,
    onSelectEvent,
    onEditEvent,
    onDuplicateEvent,
    onCancelEvent,
    onDeleteEvent,
  ]);

  const wrapperClass = ['vq-cal', isMobile ? 'is-mobile' : '', className]
    .filter(Boolean)
    .join(' ');

  const renderCtx: CalendarRenderContext = useMemo(
    () => ({
      events: filteredEvents,
      today: todayKey,
      cursor,
      selectedDate,
      kindTheme,
      groups: safeGroups,
      locale,
      weekStartsOn,
      viewerTz,
      onCursorChange: setCursor,
      onSelectedDateChange: setSelectedDate,
      onSelectEvent: handleEventTap,
    }),
    // setCursor / setSelectedDate / handleEventTap close over local
    // state setters — recompute when their inputs change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      filteredEvents,
      todayKey,
      cursor,
      selectedDate,
      kindTheme,
      safeGroups,
      locale,
      weekStartsOn,
      viewerTz,
      isMobile,
    ],
  );

  // Shared props for the view-mode dispatch. Each view component picks
  // the subset it needs; bundling here avoids a 5-way duplication of
  // the same prop chain across the JSX branches below.
  const sharedViewProps = {
    cursor,
    onCursorChange: setCursor,
    selectedDate,
    onSelectedDateChange: setSelectedDate,
    today: todayKey,
    events: filteredEvents,
    kindTheme,
    groups: safeGroups,
    locale,
    weekStartsOn,
    viewerTz,
    onSelectEvent: handleEventTap,
    onSelectLink,
    renderDayRailExtras,
    renderEventExtras,
    contextMenu: buildContextMenu,
    selectedEventKey,
  } as const;

  return (
    <CalendarI18nProvider value={messages}>
    <div className={wrapperClass} ref={containerRef}>
      <CalendarViewToolbar
        title={resolvedTitle}
        showTitle={showToolbarTitle}
        messages={messages}
        filteredEvents={filteredEvents}
        viewOptions={viewOptions}
        view={view}
        onViewChange={setView}
        toolbarExtras={toolbarExtras}
        enableICSExport={enableICSExport}
        showCreateButton={showToolbarCreateButton}
        isMobile={isMobile}
        onCreateEvent={onCreateEvent !== undefined ? () => { onCreateEvent(); } : undefined}
      />

      <div className="vq-cal__filter-row">
        <FilterBar
          kindTheme={kindTheme}
          visibleKinds={visibleKinds}
          groups={safeGroups}
          kindValue={kindFilter}
          onKindChange={setKindFilter}
          groupValue={groupFilter}
          onGroupChange={setGroupFilter}
          statusValue={statusFilter}
          onStatusChange={setStatusFilter}
          mobile={isMobile}
        />
      </div>

      {view === 'month' &&
        (isMobile ? (
          <MonthViewMobile {...sharedViewProps} onCreateEvent={onCreateEvent} />
        ) : (
          <MonthView {...sharedViewProps} />
        ))}
      {view === 'week' &&
        (isMobile ? (
          <WeekViewMobile {...sharedViewProps} onCreateEvent={onCreateEvent} />
        ) : (
          <WeekView
            cursor={cursor}
            onCursorChange={setCursor}
            today={todayKey}
            events={filteredEvents}
            kindTheme={kindTheme}
            locale={locale}
            weekStartsOn={weekStartsOn}
            viewerTz={viewerTz}
            onSelectEvent={handleEventTap}
            onSelectDay={(d) => {
              setSelectedDate(d);
              setView('day');
            }}
            onUpdateEvent={onUpdateEvent}
            canUpdateEvent={(event) => actionAllowed(event, 'canUpdate')}
            onCreateEvent={onCreateEvent}
            contextMenu={buildContextMenu}
            selectedEventKey={selectedEventKey}
          />
        ))}
      {view === 'day' && (
        <DayView
          {...sharedViewProps}
          mobile={isMobile}
          onUpdateEvent={onUpdateEvent}
          canUpdateEvent={(event) => actionAllowed(event, 'canUpdate')}
          onCreateEvent={onCreateEvent}
        />
      )}
      {view === 'agenda' && (
        <AgendaView
          events={filteredEvents}
          today={todayKey}
          kindTheme={kindTheme}
          groups={safeGroups}
          locale={locale}
          viewerTz={viewerTz}
          onSelectEvent={handleEventTap}
          onSelectLink={onSelectLink}
          weekStartsOn={weekStartsOn}
          renderEventExtras={renderEventExtras}
          contextMenu={buildContextMenu}
          selectedEventKey={selectedEventKey}
        />
      )}

      {customViewMap.has(view) && customViewMap.get(view)!.render(renderCtx)}

      {isMobile && onCreateEvent !== undefined && (
        <CalendarFab onClick={() => { onCreateEvent({ startsAt: selectedDate }); }} />
      )}

      {isMobile && (
        <EventDetailSheet
          event={sheetEvent}
          open={sheetEvent !== null}
          onOpenChange={(open) => {
            if (!open) setSheetEvent(null);
          }}
          kindTheme={kindTheme}
          groups={safeGroups}
          locale={locale}
          viewerTz={viewerTz}
          {...(sheetEvent !== null &&
          onSelectEvent !== undefined &&
          actionAllowed(sheetEvent, 'canOpen')
            ? { onOpen: onSelectEvent }
            : {})}
          {...(sheetEvent !== null &&
          onEditEvent !== undefined &&
          actionAllowed(sheetEvent, 'canEdit')
            ? { onEdit: onEditEvent }
            : {})}
          {...(sheetEvent !== null &&
          onDeleteEvent !== undefined &&
          actionAllowed(sheetEvent, 'canDelete')
            ? { onDelete: onDeleteEvent }
            : {})}
          onSelectLink={onSelectLink}
        />
      )}

      {composer}
    </div>
    </CalendarI18nProvider>
  );
}
